export const addBusinessDays = (startDateStr, dueDays) => {
  try {
    const [yr, mo, dy] = startDateStr.split("-").map(Number);
    const date = new Date(yr, mo - 1, dy);
    let count = 0;
    while (count < dueDays) {
      date.setDate(date.getDate() + 1);
      const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        count++;
      }
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  } catch {
    return startDateStr;
  }
};

export const addCalendarDays = (startDateStr, days = 30) => {
  try {
    const [yr, mo, dy] = startDateStr.split("-").map(Number);
    const date = new Date(yr, mo - 1, dy);
    date.setDate(date.getDate() + Number(days));
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  } catch {
    return startDateStr;
  }
};

/**
 * Safely parses any Firestore Timestamp, ISO string, epoch millisecond, or Date object into a JavaScript Date.
 * @param {any} value - The timestamp value (Firestore Timestamp, Date, string, number, or object with seconds)
 * @param {any} fallback - Optional fallback value if parsing fails or value is null/undefined
 * @returns {Date|null} Valid Date object or null if parsing fails
 */
export const parseFirestoreTimestamp = (value, fallback = null) => {
  if (!value && fallback) {
    return parseFirestoreTimestamp(fallback, null);
  }
  if (!value) return null;

  try {
    // 1. Instance of Date
    if (value instanceof Date) {
      return isNaN(value.getTime()) ? null : value;
    }

    // 2. Firestore Timestamp instance (has .toDate() method)
    if (typeof value.toDate === "function") {
      const d = value.toDate();
      return isNaN(d.getTime()) ? null : d;
    }

    // 3. Object with seconds / _seconds property (Firestore Timestamp plain object representation)
    if (typeof value === "object") {
      const seconds = value.seconds ?? value._seconds;
      const nanoseconds = value.nanoseconds ?? value._nanoseconds ?? 0;
      if (typeof seconds === "number") {
        const d = new Date(seconds * 1000 + Math.floor(nanoseconds / 1000000));
        return isNaN(d.getTime()) ? null : d;
      }
    }

    // 4. Number (epoch milliseconds or epoch seconds if small)
    if (typeof value === "number") {
      const ms = value < 10000000000 ? value * 1000 : value;
      const d = new Date(ms);
      return isNaN(d.getTime()) ? null : d;
    }

    // 5. String (ISO 8601, UTC string, or date string)
    if (typeof value === "string") {
      const d = new Date(value);
      if (!isNaN(d.getTime())) return d;
    }
  } catch {
    // Ignore and proceed to fallback
  }

  if (fallback) {
    return parseFirestoreTimestamp(fallback, null);
  }
  return null;
};

/**
 * Formats a Firestore timestamp into a localized or UTC readable string for audit displays.
 * @param {any} value - Timestamp value
 * @param {any} fallback - Fallback value
 * @returns {string} Formatted string or "N/A"
 */
export const formatAuditTimestamp = (value, fallback = null) => {
  const d = parseFirestoreTimestamp(value, fallback);
  if (!d) return "N/A";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true
  });
};

/**
 * Formats a timestamp into an ISO 8601 string for CSV/JSON compliance exports.
 * @param {any} value - Timestamp value
 * @param {any} fallback - Fallback value
 * @returns {string} ISO 8601 string or ""
 */
export const toISOTimestamp = (value, fallback = null) => {
  const d = parseFirestoreTimestamp(value, fallback);
  return d ? d.toISOString() : "";
};

/**
 * Formats a user's last login timestamp into a clean, relative and readable format.
 * Returns "Today, HH:MM AM/PM", "Yesterday, HH:MM AM/PM", "MMM DD, HH:MM AM/PM", or "Never".
 * @param {any} value - Timestamp value (Firestore Timestamp, ISO string, epoch ms)
 * @returns {string} Formatted string
 */
export const formatLastLoginDate = (value) => {
  const d = parseFirestoreTimestamp(value);
  if (!d) return "Never";

  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();

  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();

  const timeStr = d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

  if (isToday) {
    return `Today, ${timeStr}`;
  }
  if (isYesterday) {
    return `Yesterday, ${timeStr}`;
  }

  const isCurrentYear = d.getFullYear() === now.getFullYear();
  const dateStr = d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: isCurrentYear ? undefined : "numeric"
  });

  return `${dateStr}, ${timeStr}`;
};


